import { decodeHTMLEntity } from "@/common/helpers/encoders";
import { SpeakerRole } from "@/types/external-content";
import { MediaPlayerTextTrackKind } from "@/types/media-player";
import { TranscriptSource } from "@/types/transcript";
// system_message_type values that flag a backroom (moderator-only) system row;
// `type === BACKROOM_MESSAGE_TYPE` flags a backroom chat message. Verified
// against legacy message-stream.js; Group 3 reconciles against the live host.
const BACKROOM_SYSTEM_TYPES = new Set([11, 16]);
const BACKROOM_MESSAGE_TYPE = 2;
// system_message_type ids for the wait-room lifecycle (ChatMessage.SystemMessage
// WAIT_ROOM_* 100-104). Included in the transcript payload for study admins only.
const WAIT_ROOM_SYSTEM_TYPES = new Set([100, 101, 102, 103, 104]);
// Private DMs (PRIVATE, ASSIST_PRIVATE) never belong in the shared transcript.
const PRIVATE_MESSAGE_TYPES = new Set([1, 5]);
const BLOCK_BREAK_TAGS = /<\/(?:p|div|li|h[1-6])>|<br\s*\/?>/gi;
const isBackroomMessage = (message) => message.type === BACKROOM_MESSAGE_TYPE ||
    (message.system_message_type !== undefined && BACKROOM_SYSTEM_TYPES.has(message.system_message_type));
const isWaitingRoomMessage = (message) => message.system_message_type !== undefined && WAIT_ROOM_SYSTEM_TYPES.has(message.system_message_type);
// Any non-system, non-transcript chat message that belongs in the stream: a plain
// typed message (the whole conversation in a text chat), a backroom comment, or an
// admin "sent in chat" upload. Private DMs and empty messages are excluded.
const isRenderableChatMessage = (message) => {
    if (message.system_message || message.transcript)
        return false;
    if (message.type !== undefined && PRIVATE_MESSAGE_TYPES.has(message.type))
        return false;
    const hasText = stripHtml(message.content ?? message.message ?? "").length > 0;
    const hasAssets = Array.isArray(message.assets) && message.assets.length > 0;
    const hasPoll = message.poll !== undefined;
    return hasText || hasAssets || hasPoll;
};
// Flattens a chat body for `segment.text` (search, SR label, emptiness) — never for rendering; block tags become spaces so words don't merge across paragraphs.
const stripHtml = (html) => decodeHTMLEntity(html.replace(BLOCK_BREAK_TAGS, " ").replace(/<[^>]*>/g, "")).trim();
const toAssets = (assets) => {
    if (!Array.isArray(assets) || assets.length === 0)
        return undefined;
    return assets.map((asset) => ({
        assetId: asset.asset_id ?? 0,
        type: asset.type === "VIDEO" || asset.type === "FILE" ? asset.type : "PHOTO",
        fileUrl: asset.file_url ?? asset.photo_url,
        videoUrl: asset.video_url,
        posterImg: asset.poster_img,
        caption: asset.caption !== undefined ? stripHtml(asset.caption) : undefined,
        filename: asset.filename
    }));
};
// Map an admin chat poll, resolving each choice's respondent ids to profile
// photos/names from the meeting users (skipped for anonymous polls).
const toPoll = (poll, users) => {
    if (!poll)
        return undefined;
    const anon = poll.anon === true;
    return {
        id: poll.id ?? 0,
        question: poll.question ?? "",
        instructions: poll.instructions,
        anon,
        required: poll.required === true,
        choices: (poll.choices ?? []).map((choice) => ({
            id: choice.id ?? 0,
            value: choice.value ?? "",
            count: choice.count ?? 0,
            percent: choice.percent ?? 0,
            skipped: choice.skipped,
            respondents: anon
                ? []
                : (choice.users ?? []).map((userId) => ({
                    photo: users[userId]?.profile_photo,
                    name: users[userId]?.display_name
                }))
        }))
    };
};
// Language label when a message belongs to a translator channel (IDI
// multi-language meetings); undefined for the primary/spoken channel. Drives
// per-translation-channel filtering.
const translationChannelLabel = (message, channels) => {
    if (message.channel === undefined)
        return undefined;
    const channel = channels[message.channel];
    if (!channel?.translator)
        return undefined;
    return channel.language_name ?? channel.language;
};
// Legacy `results` is either a flat word list or a list of word groups; flatten
// either into one ordered list so a message becomes a single segment.
const flattenWords = (results) => {
    if (!Array.isArray(results))
        return [];
    const flat = [];
    for (const entry of results) {
        if (Array.isArray(entry))
            flat.push(...entry);
        else if (entry)
            flat.push(entry);
    }
    return flat;
};
// Stable per-message speaker key: the user's display name, else a label derived
// from user_id, else "unknown". TranscriptLayout groups consecutive segments by
// this key, so it must be deterministic.
const speakerKeyFor = (message, users) => {
    const userId = message.user_id;
    if (userId !== undefined) {
        const name = users[userId]?.display_name;
        if (name)
            return name;
        return `participant_${userId}`;
    }
    return "unknown";
};
const toChunk = (word, speaker) => ({
    word: word.text ?? "",
    start: word.start ?? 0,
    end: word.end ?? 0,
    speaker,
    // Caption token index (server `aidx`); the excerpt-create markup emits
    // idx=index*2 (word) / index*2+1 (space) so Ramius capture aligns with the
    // server's getTagText(index). Undefined-tolerant for partial payloads.
    index: word.index,
    // Result-group/word coordinates the legacy `TranscribeEdit` WS action needs to
    // persist an inline word edit. Preserved through the group-flatten above.
    groupindex: word.groupindex,
    wordindex: word.wordindex
});
// System rows carry a wall-clock epoch-ms timestamp; transcript words carry
// recording-relative seconds. To put both on one axis, anchor off the first
// transcript message that has both a `timestamp` and a leading word `start`:
// meetingStartMs = timestamp - firstWordStart*1000. A text chat has no such
// spoken row (no recording), so fall back to the earliest message timestamp —
// offsets then read as time-since-conversation-start. Returns undefined only
// when no message carries a timestamp at all.
const deriveMeetingStartMs = (messages) => {
    for (const message of messages) {
        if (!message.transcript || message.timestamp === undefined)
            continue;
        const firstWord = flattenWords(message.results)[0];
        if (firstWord?.start === undefined)
            continue;
        return message.timestamp - firstWord.start * 1000;
    }
    let earliest;
    for (const message of messages) {
        if (message.timestamp === undefined)
            continue;
        if (earliest === undefined || message.timestamp < earliest)
            earliest = message.timestamp;
    }
    return earliest;
};
// Every translator channel's label, whether or not any message landed on it.
const buildTranslationChannels = (channels) => {
    const labels = new Set();
    for (const channel of Object.values(channels)) {
        if (!channel.translator)
            continue;
        const label = channel.language_name ?? channel.language;
        if (label)
            labels.add(label);
    }
    return [...labels];
};
const buildTextTracks = (channels, defaultLanguage) => {
    const tracks = [];
    for (const channel of Object.values(channels)) {
        if (!channel.vtt_url)
            continue;
        const srclang = channel.language ?? "";
        tracks.push({
            kind: MediaPlayerTextTrackKind.Captions,
            src: channel.vtt_url,
            srclang,
            label: channel.language_name ?? srclang,
            default: defaultLanguage !== undefined && srclang === defaultLanguage
        });
    }
    return tracks;
};
/**
 * Pure mapper: legacy IDI "load detail" WS payload → the shared transcript model.
 * Tolerant of missing/empty arrays so a partial payload never throws.
 *
 * `backroomAccess` is the single place backroom content is gated FE-side. The
 * backend already excludes backroom rows from the payload for non-permitted
 * users (ChatMessage query filter); this is defense-in-depth / UI-consistency,
 * not the primary control. Defaults to true so existing callers are unchanged.
 */
export const mapLoadDetailToTranscript = (payload, backroomAccess = true) => {
    const meeting = payload.meeting ?? {};
    const messages = meeting.messages ?? [];
    const users = meeting.users ?? {};
    const channels = meeting.channels ?? {};
    const segments = [];
    const systemRows = [];
    const speakers = {};
    const participants = {};
    let hostCount = 0;
    let attendeeCount = 0;
    const meetingStartMs = deriveMeetingStartMs(messages);
    const relativeStartFor = (timestamp) => meetingStartMs !== undefined && timestamp !== undefined ? (timestamp - meetingStartMs) / 1000 : 0;
    // Register a speaker (display name + role + counts) and its IDI participant
    // metadata (profile + channel) once. Shared by the spoken and chat paths.
    const registerSpeaker = (speaker, message) => {
        const user = users[message.user_id ?? ""];
        if (!(speaker in speakers)) {
            const isHost = message.host === true || user?.admin === true || user?.client === true;
            speakers[speaker] = {
                displayName: user?.display_name ?? speaker,
                role: isHost ? SpeakerRole.Host : SpeakerRole.Attendee,
                inactive: !!user?.inactive
            };
            if (isHost)
                hostCount++;
            else
                attendeeCount++;
        }
        if (!(speaker in participants)) {
            participants[speaker] = {
                profileUrl: user?.profile_url,
                profilePhoto: user?.profile_photo,
                channel: message.channel
            };
        }
        else if (participants[speaker].channel === undefined && message.channel !== undefined) {
            participants[speaker].channel = message.channel;
        }
    };
    for (const message of messages) {
        // Suppress every backroom row (system + transcript) when the viewer lacks access.
        if (!backroomAccess && isBackroomMessage(message))
            continue;
        if (message.system_message) {
            const backroom = isBackroomMessage(message);
            const timestamp = message.timestamp ?? 0;
            systemRows.push({
                text: message.message ?? message.content ?? "",
                timestamp,
                isBackroom: backroom,
                isWaitingRoom: isWaitingRoomMessage(message),
                relativeStart: meetingStartMs !== undefined && message.timestamp !== undefined
                    ? (timestamp - meetingStartMs) / 1000
                    : undefined
            });
            continue;
        }
        // Chat in the stream (typed messages, backroom comments, "sent in chat" uploads): the row is the server's own markup, so no per-word chunks.
        if (!message.transcript) {
            if (!isRenderableChatMessage(message))
                continue;
            const speaker = speakerKeyFor(message, users);
            const backroom = message.type === BACKROOM_MESSAGE_TYPE;
            const body = message.content ?? message.message ?? "";
            const text = stripHtml(body);
            const start = relativeStartFor(message.timestamp);
            segments.push({
                id: message.id ?? message.index ?? segments.length,
                text,
                start,
                end: start,
                speaker,
                words: [],
                content: body,
                sourceIndex: message.index,
                source: backroom ? TranscriptSource.Backroom : TranscriptSource.Chat,
                assets: toAssets(message.assets),
                poll: toPoll(message.poll, users),
                highlights: message.highlights,
                isBackroom: backroom
            });
            registerSpeaker(speaker, message);
            continue;
        }
        const speaker = speakerKeyFor(message, users);
        const words = flattenWords(message.results).map((word) => toChunk(word, speaker));
        const start = words.length > 0 ? words[0].start : 0;
        const end = words.length > 0 ? words[words.length - 1].end : start;
        segments.push({
            id: message.id ?? message.index ?? segments.length,
            text: words.map((word) => word.word).join(" "),
            start,
            end,
            speaker,
            words,
            // Legacy message index → Ramius focus-area id (rc-chat-{room}-t-{index}).
            sourceIndex: message.index,
            // Server-rendered excerpt overlay HTML (existing excerpts' <q> markup).
            highlights: message.highlights,
            // Translator-channel language label (multi-language meetings only).
            channelLabel: translationChannelLabel(message, channels)
        });
        registerSpeaker(speaker, message);
    }
    const blocks = segments.length > 0 ? [{ id: 0, language: meeting.default_language ?? null, segments }] : [];
    const transcript = {
        blocks,
        metadata: { attendeeCount, hostCount },
        speakers
    };
    return {
        transcript,
        systemRows,
        textTracks: buildTextTracks(channels, meeting.default_language),
        participants,
        translationChannels: buildTranslationChannels(channels)
    };
};
